import { useState, useEffect } from 'react';
import {
  User as UserIcon,
  Shield,
  Lock,
  Settings,
  Loader2,
  Eye,
  EyeOff,
  Send,
  ImageIcon,
  Trash2,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from '@/components/ui/toaster';
import { normalizeError } from '@/lib/api';
import { cn, getInitials } from '@/lib/utils';
import {
  useUserAdminDetails,
  useAdminUpdateUser,
  useAdminSetPassword,
  useAdminSendPasswordReset,
  useAdminUpdateUserTeams,
  useAdminUpdateUserProjects,
} from '@/hooks/use-users';
import { useTeams } from '@/hooks/use-teams';
import { useProjects } from '@/hooks/use-projects';
import type { GlobalRole } from '@/types';

const ROLE_LABELS: Record<GlobalRole, string> = {
  admin: 'Admin',
  supervisor: 'Supervisor',
  user: 'User',
  client: 'Client',
};

const STANDARD_AVATARS = [
  '/avatars/milovanderlinden-servicedesk-employee.svg',
  '/avatars/msewtz-Business-Person.svg',
  '/avatars/nicubunu_Comic_characters_Bearded_guy.svg',
  '/avatars/nicubunu_Comic_characters_Blonde.svg',
  '/avatars/nicubunu_Comic_characters_Pirate.svg',
  '/avatars/chovynz-OCAL-Userpic-RobinHood.svg',
  '/avatars/nicubunu_Game_baddie_Nurse.svg',
  '/avatars/nicubunu_Game_baddie_Santa.svg',
  '/avatars/HappyPandaFace.svg',
  '/avatars/Little-funny-penguin.svg',
  '/avatars/Bee-6.svg',
  '/avatars/funny-frog.svg',
  '/avatars/funny-girl.svg',
  '/avatars/boy4.svg',
  '/avatars/brown-funny-monkey.svg',
  '/avatars/cochon.svg',
];

interface UserManagementModalProps {
  userId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function UserManagementModal({ userId, open, onOpenChange }: UserManagementModalProps) {
  const { data: user, isLoading } = useUserAdminDetails(userId || undefined);
  const { data: teams = [] } = useTeams();
  const { data: projects = [] } = useProjects();

  const adminUpdate = useAdminUpdateUser();
  const setPassword = useAdminSetPassword();
  const sendPasswordReset = useAdminSendPasswordReset();
  const updateTeams = useAdminUpdateUserTeams();
  const updateProjects = useAdminUpdateUserProjects();

  // Profile state
  const [displayName, setDisplayName] = useState('');
  const [publicName, setPublicName] = useState('');
  const [email, setEmail] = useState('');
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [showAvatarPicker, setShowAvatarPicker] = useState(false);

  // Access state
  const [role, setRole] = useState<GlobalRole>('user');
  const [isActive, setIsActive] = useState(true);
  const [selectedTeamIds, setSelectedTeamIds] = useState<string[]>([]);
  const [selectedProjectIds, setSelectedProjectIds] = useState<string[]>([]);

  // Security state
  const [newPassword, setNewPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  // Preferences state
  const [emailNotifications, setEmailNotifications] = useState(true);

  // Init state from user data
  useEffect(() => {
    if (user) {
      setDisplayName(user.displayName);
      setPublicName(user.publicName || '');
      setEmail(user.email);
      setAvatarUrl(user.avatarUrl);
      setRole(user.role);
      setIsActive(user.isActive);
      setSelectedTeamIds(user.teamIds);
      setSelectedProjectIds(user.projectIds);
      setEmailNotifications(user.emailNotificationsEnabled);
    }
  }, [user]);

  const handleSaveProfile = async () => {
    if (!userId) return;
    try {
      await adminUpdate.mutateAsync({
        id: userId,
        data: {
          displayName,
          publicName: publicName || null,
          email,
          avatarUrl,
        },
      });
      toast({ title: 'Profile updated' });
    } catch (error) {
      toast({
        title: 'Failed to update profile',
        description: normalizeError(error).message,
        variant: 'destructive',
      });
    }
  };

  const handleSaveAccess = async () => {
    if (!userId) return;
    try {
      // Update role and status
      await adminUpdate.mutateAsync({
        id: userId,
        data: { role, isActive },
      });

      // Update teams if changed
      const teamsChanged =
        JSON.stringify([...selectedTeamIds].sort()) !==
        JSON.stringify([...user!.teamIds].sort());
      if (teamsChanged) {
        await updateTeams.mutateAsync({ id: userId, teamIds: selectedTeamIds });
      }

      // Update projects if changed
      const projectsChanged =
        JSON.stringify([...selectedProjectIds].sort()) !==
        JSON.stringify([...user!.projectIds].sort());
      if (projectsChanged) {
        await updateProjects.mutateAsync({ id: userId, projectIds: selectedProjectIds });
      }

      toast({ title: 'Access settings updated' });
    } catch (error) {
      toast({
        title: 'Failed to update access',
        description: normalizeError(error).message,
        variant: 'destructive',
      });
    }
  };

  const handleSetPassword = async () => {
    if (!userId || !newPassword) return;
    if (newPassword.length < 8) {
      toast({
        title: 'Password too short',
        description: 'Password must be at least 8 characters',
        variant: 'destructive',
      });
      return;
    }
    try {
      await setPassword.mutateAsync({ id: userId, password: newPassword });
      setNewPassword('');
      toast({ title: 'Password updated' });
    } catch (error) {
      toast({
        title: 'Failed to set password',
        description: normalizeError(error).message,
        variant: 'destructive',
      });
    }
  };

  const handleSendPasswordReset = async () => {
    if (!userId) return;
    try {
      const result = await sendPasswordReset.mutateAsync(userId);
      toast({
        title: result.success ? 'Password reset email sent' : 'Failed to send',
        description: result.message,
        variant: result.success ? 'default' : 'destructive',
      });
    } catch (error) {
      toast({
        title: 'Failed to send reset email',
        description: normalizeError(error).message,
        variant: 'destructive',
      });
    }
  };

  const handleSavePreferences = async () => {
    if (!userId) return;
    try {
      await adminUpdate.mutateAsync({
        id: userId,
        data: { emailNotificationsEnabled: emailNotifications },
      });
      toast({ title: 'Preferences updated' });
    } catch (error) {
      toast({
        title: 'Failed to update preferences',
        description: normalizeError(error).message,
        variant: 'destructive',
      });
    }
  };

  const toggleTeam = (teamId: string) => {
    setSelectedTeamIds((prev) =>
      prev.includes(teamId) ? prev.filter((id) => id !== teamId) : [...prev, teamId]
    );
  };

  const toggleProject = (projectId: string) => {
    setSelectedProjectIds((prev) =>
      prev.includes(projectId)
        ? prev.filter((id) => id !== projectId)
        : [...prev, projectId]
    );
  };

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            {user && (
              <>
                <Avatar className="h-10 w-10">
                  <AvatarImage src={user.avatarUrl || undefined} />
                  <AvatarFallback>{getInitials(user.displayName)}</AvatarFallback>
                </Avatar>
                <div>
                  <p className="text-lg">{user.displayName}</p>
                  <p className="text-sm font-normal text-muted-foreground">{user.email}</p>
                </div>
              </>
            )}
          </DialogTitle>
        </DialogHeader>

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : user ? (
          <Tabs defaultValue="profile" className="flex-1 flex flex-col min-h-0">
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="profile" className="gap-2">
                <UserIcon className="h-4 w-4" />
                <span className="hidden sm:inline">Profile</span>
              </TabsTrigger>
              <TabsTrigger value="access" className="gap-2">
                <Shield className="h-4 w-4" />
                <span className="hidden sm:inline">Access</span>
              </TabsTrigger>
              <TabsTrigger value="security" className="gap-2">
                <Lock className="h-4 w-4" />
                <span className="hidden sm:inline">Security</span>
              </TabsTrigger>
              <TabsTrigger value="preferences" className="gap-2">
                <Settings className="h-4 w-4" />
                <span className="hidden sm:inline">Preferences</span>
              </TabsTrigger>
            </TabsList>

            {/* Profile Tab */}
            <TabsContent value="profile" className="flex-1 overflow-auto">
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="displayName">Real Name (internal)</Label>
                  <Input
                    id="displayName"
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    placeholder="Full name"
                  />
                  <p className="text-xs text-muted-foreground">
                    Visible to internal users (admins, supervisors, agents)
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="publicName">Public Name (clients see this)</Label>
                  <Input
                    id="publicName"
                    value={publicName}
                    onChange={(e) => setPublicName(e.target.value)}
                    placeholder="Display name for clients"
                  />
                  <p className="text-xs text-muted-foreground">
                    Clients will see this name. If empty, they see the real name.
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Avatar</Label>
                  <div className="flex items-center gap-4">
                    <Avatar className="h-16 w-16">
                      <AvatarImage src={avatarUrl || undefined} />
                      <AvatarFallback className="text-xl">
                        {getInitials(displayName)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex flex-col gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setShowAvatarPicker(!showAvatarPicker)}
                      >
                        <ImageIcon className="h-4 w-4 mr-2" />
                        Choose Avatar
                      </Button>
                      {avatarUrl && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setAvatarUrl(null)}
                          className="text-destructive"
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Remove
                        </Button>
                      )}
                    </div>
                  </div>

                  {showAvatarPicker && (
                    <div className="grid grid-cols-4 gap-2 p-3 border rounded-lg mt-2">
                      {STANDARD_AVATARS.map((url) => (
                        <button
                          key={url}
                          type="button"
                          onClick={() => {
                            setAvatarUrl(url);
                            setShowAvatarPicker(false);
                          }}
                          className={cn(
                            'p-2 rounded-lg hover:bg-muted transition-colors flex items-center justify-center',
                            avatarUrl === url && 'ring-2 ring-primary'
                          )}
                        >
                          <img
                            src={url}
                            alt="Avatar option"
                            className="h-12 w-12 rounded-full object-cover"
                            onError={(e) => {
                              (e.target as HTMLImageElement).style.display = 'none';
                            }}
                          />
                        </button>
                      ))}
                    </div>
                  )}

                  <div className="space-y-2 mt-2">
                    <Label htmlFor="customAvatar">Custom Avatar URL</Label>
                    <Input
                      id="customAvatar"
                      value={avatarUrl || ''}
                      onChange={(e) => setAvatarUrl(e.target.value || null)}
                      placeholder="https://..."
                    />
                  </div>
                </div>

                <div className="flex justify-end pt-4">
                  <Button onClick={handleSaveProfile} disabled={adminUpdate.isPending}>
                    {adminUpdate.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                    Save Profile
                  </Button>
                </div>
              </div>
            </TabsContent>

            {/* Access Tab */}
            <TabsContent value="access" className="flex-1 overflow-auto">
              <ScrollArea className="h-[400px]">
                <div className="space-y-6 py-4 pr-4">
                  <div className="space-y-2">
                    <Label>Role</Label>
                    <Select value={role} onValueChange={(v) => setRole(v as GlobalRole)}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(ROLE_LABELS).map(([value, label]) => (
                          <SelectItem key={value} value={value}>
                            {label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="flex items-center justify-between">
                    <div>
                      <Label>Status</Label>
                      <p className="text-sm text-muted-foreground">
                        {isActive ? 'User can log in' : 'User is deactivated'}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant={isActive ? 'default' : 'secondary'}>
                        {isActive ? 'Active' : 'Inactive'}
                      </Badge>
                      <Switch checked={isActive} onCheckedChange={setIsActive} />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Teams</Label>
                    <div className="border rounded-lg p-3 space-y-2 max-h-[150px] overflow-y-auto">
                      {teams.length === 0 ? (
                        <p className="text-sm text-muted-foreground">No teams available</p>
                      ) : (
                        teams.map((team) => (
                          <label
                            key={team.id}
                            className="flex items-center gap-3 p-2 rounded hover:bg-muted cursor-pointer"
                          >
                            <Checkbox
                              checked={selectedTeamIds.includes(team.id)}
                              onCheckedChange={() => toggleTeam(team.id)}
                            />
                            <div>
                              <p className="font-medium">{team.name}</p>
                              <p className="text-xs text-muted-foreground">
                                {team.memberCount} member{team.memberCount !== 1 ? 's' : ''}
                              </p>
                            </div>
                          </label>
                        ))
                      )}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Projects / Client Access</Label>
                    <div className="border rounded-lg p-3 space-y-2 max-h-[150px] overflow-y-auto">
                      {projects.length === 0 ? (
                        <p className="text-sm text-muted-foreground">No projects available</p>
                      ) : (
                        projects.map((project) => (
                          <label
                            key={project.id}
                            className="flex items-center gap-3 p-2 rounded hover:bg-muted cursor-pointer"
                          >
                            <Checkbox
                              checked={selectedProjectIds.includes(project.id)}
                              onCheckedChange={() => toggleProject(project.id)}
                            />
                            <div className="flex items-center gap-2">
                              <div
                                className="h-3 w-3 rounded-sm"
                                style={{ backgroundColor: project.color }}
                              />
                              <p className="font-medium">{project.name}</p>
                            </div>
                          </label>
                        ))
                      )}
                    </div>
                  </div>

                  <div className="flex justify-end pt-4">
                    <Button
                      onClick={handleSaveAccess}
                      disabled={adminUpdate.isPending || updateTeams.isPending || updateProjects.isPending}
                    >
                      {(adminUpdate.isPending || updateTeams.isPending || updateProjects.isPending) && (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      )}
                      Save Access
                    </Button>
                  </div>
                </div>
              </ScrollArea>
            </TabsContent>

            {/* Security Tab */}
            <TabsContent value="security" className="flex-1">
              <div className="space-y-6 py-4">
                <div className="space-y-4">
                  <div>
                    <h4 className="font-medium mb-1">Set New Password</h4>
                    <p className="text-sm text-muted-foreground mb-3">
                      Manually set a new password for this user. They will be logged out of all sessions.
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <Input
                        type={showPassword ? 'text' : 'password'}
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        placeholder="Enter new password (min 8 characters)"
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="absolute right-0 top-0 h-full"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? (
                          <EyeOff className="h-4 w-4" />
                        ) : (
                          <Eye className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                    <Button
                      onClick={handleSetPassword}
                      disabled={setPassword.isPending || !newPassword}
                    >
                      {setPassword.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                      Set Password
                    </Button>
                  </div>
                </div>

                <div className="border-t pt-6">
                  <div>
                    <h4 className="font-medium mb-1">Send Password Reset Link</h4>
                    <p className="text-sm text-muted-foreground mb-3">
                      Send an email with a password reset link to the user.
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    onClick={handleSendPasswordReset}
                    disabled={sendPasswordReset.isPending || !user.isActive}
                  >
                    {sendPasswordReset.isPending ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Send className="h-4 w-4 mr-2" />
                    )}
                    Send Reset Link
                  </Button>
                  {!user.isActive && (
                    <p className="text-xs text-destructive mt-2">
                      Cannot send reset link to inactive user
                    </p>
                  )}
                </div>
              </div>
            </TabsContent>

            {/* Preferences Tab */}
            <TabsContent value="preferences" className="flex-1">
              <div className="space-y-6 py-4">
                <div className="flex items-center justify-between">
                  <div>
                    <Label>Email Notifications</Label>
                    <p className="text-sm text-muted-foreground">
                      Receive email notifications for assignments, mentions, and updates
                    </p>
                  </div>
                  <Switch
                    checked={emailNotifications}
                    onCheckedChange={setEmailNotifications}
                  />
                </div>

                <div className="flex justify-end pt-4">
                  <Button onClick={handleSavePreferences} disabled={adminUpdate.isPending}>
                    {adminUpdate.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                    Save Preferences
                  </Button>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        ) : (
          <div className="py-12 text-center text-muted-foreground">User not found</div>
        )}
      </DialogContent>
    </Dialog>
  );
}
